/**
 * @fileoverview Shared wire types for the Discover saved-search backend
 * (FRE-926).
 *
 * A "saved search" persists the canonical serialized Discover URL state
 * (`serializeDiscoverParams` output) plus a human label so the authenticated
 * saved-searches rail (FRE-916) can re-run it and render a NEW badge. The
 * NEW badge is driven by `newCount` — the number of events matching the saved
 * query that were created since the search was last run.
 *
 * These types are the contract between the backend route
 * (`/api/v1/users/me/saved-searches`) and any client (web RTK Query, mobile).
 *
 * @since FRE-926 (Discover personalization foundation — saved-search backend)
 */

/**
 * A persisted saved search as returned by the create / run endpoints. Mirrors
 * the `SavedSearch` Prisma row with timestamps serialized to ISO 8601 strings
 * on the wire.
 */
export interface SavedSearch {
  /** cuid primary key. */
  id: string;
  /** Owning user id. */
  userId: string;
  /** Human-readable label shown in the rail. */
  label: string;
  /**
   * Canonical serialized Discover URL state (`serializeDiscoverParams` output,
   * e.g. `"category=music&date=this-weekend"`). Stored and returned verbatim so
   * re-running reuses the single-fetch cold-load cache key.
   */
  queryState: string;
  /** ISO 8601 creation timestamp. */
  createdAt: string;
  /** ISO 8601 timestamp of the last run; anchors the NEW-since delta. */
  lastRunAt: string;
}

/**
 * A saved search enriched with the NEW-since-last-run delta. Returned by the
 * list endpoint so the rail can render the NEW badge without a second call.
 */
export interface SavedSearchWithNewCount extends SavedSearch {
  /**
   * Number of future events matching `queryState` that were created after
   * `lastRunAt`. Drives the rail's NEW badge. `0` when nothing new (the badge
   * is hidden), never negative.
   */
  newCount: number;
}

/**
 * Request body for `POST /api/v1/users/me/saved-searches`.
 */
export interface CreateSavedSearchRequest {
  /** Human-readable label (1–120 chars after trim). */
  label: string;
  /**
   * Canonical serialized Discover URL state. The client passes the already
   * serialized string (`serializeDiscoverParams(...).toString()`); the backend
   * stores it verbatim.
   */
  queryState: string;
}

/**
 * Response body for `GET /api/v1/users/me/saved-searches`.
 */
export interface ListSavedSearchesResponse {
  savedSearches: SavedSearchWithNewCount[];
}

/**
 * Response body for `DELETE /api/v1/users/me/saved-searches/:id`.
 */
export interface DeleteSavedSearchResponse {
  id: string;
  deleted: true;
}
